import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import DialogContent from "@mui/material/DialogContent";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import LaptopIcon from "@mui/icons-material/Laptop";
import { useQuery, useMutation } from "@apollo/client";
import { ViewerSessionsQuery } from "core/queries";
import CircularProgress from "@mui/material/CircularProgress";
import ErrorScreen from "core/components/ErrorScreen";
import { useViewer } from "core/components/ViewerProvider";
import { CloseSessionsMutation } from "core/mutations";
import DialogHeader from "core/components/DialogHeader";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Checkbox from "@mui/material/Checkbox";

const PREFIX = "ClosePremiumSessionDialog";

const classes = {
  title: `${PREFIX}-title`,
  primary: `${PREFIX}-primary`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.title}`]: {
    flex: 1,
    marginLeft: 4,
  },

  [`& .${classes.primary}`]: {
    color: "white",
    backgroundColor: theme.palette.primary.main,
  },
}));

export default function ClosePremiumSessionDialog({ open, onClose }) {
  const { t } = useTranslation("core");
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [selected, setSelected] = useState([]);
  const viewer = useViewer();

  const { data, loading, error } = useQuery(ViewerSessionsQuery, {
    variables: {
      forPremiumView: true,
    },
    skip: !open || !viewer,
  });

  const [closeSessions, { loading: closeLoading }] = useMutation(
    CloseSessionsMutation,
    {
      refetchQueries: ["Content"],
      onCompleted: () => {
        onClose();
      },
    }
  );

  const sessions = data?.viewerSessions?.nodes || [];

  const toggleSelectSession = (session) => {
    if (selected.includes(session.id)) {
      const newSelected = selected.filter((sid) => sid !== session.id);
      setSelected(newSelected);
    } else {
      setSelected([session.id, ...selected]);
    }
  };

  const closeSelectedSessions = () => {
    closeSessions({ variables: { ids: selected } });
  };

  const onClickCloseSession = () => {
    closeSelectedSessions();
  };

  if (!viewer) {
    return;
  }

  const limit = viewer.currentSession.premiumContent ? 3 : 2;
  const next = sessions.length - selected.length <= limit;
  const disabled = !next || loading || closeLoading;

  return (
    <StyledDialog
      scroll="paper"
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      open={open}
    >
      <DialogHeader
        onClose={onClose}
        title={t("closeSessionsDialog.title")}
        action={
          <Button
            onClick={onClickCloseSession}
            variant="contained"
            color="primary"
            disabled={disabled}
          >
            {t("closeSessionsDialog.closeSessions")}
          </Button>
        }
      />

      <DialogContent style={{ minHeight: "300px", padding: "0px 0px 8px 0px" }}>
        {error && <ErrorScreen error={error} />}

        {loading && !data && (
          <div align="center" style={{ marginTop: theme.spacing(2) }}>
            <CircularProgress color="secondary" />
          </div>
        )}

        <List style={{ padding: "0" }}>
          {sessions.map((session) => (
            <ListItem
              key={session.id}
              button
              disabled={viewer.currentSession.id === session.id}
              selected={selected.includes(session.id)}
              onClick={() => toggleSelectSession(session)}
            >
              <ListItemAvatar>
                <Avatar className={classes.primary}>
                  {session.userDeviceType === "Mobile" ? (
                    <SmartphoneIcon />
                  ) : (
                    <LaptopIcon />
                  )}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={[session.userDeviceType, session.userDeviceOs]
                  .filter(Boolean)
                  .join(" ")}
                secondary={
                  <>
                    <Typography
                      component="span"
                      variant="body2"
                      color="textPrimary"
                      style={{ marginRight: theme.spacing(0.5) }}
                    >
                      {session.latestIp}
                    </Typography>

                    {viewer.currentSession.id === session.id
                      ? t("closeSessionsDialog.currentDevice")
                      : t("closeSessionsDialog.latestActivity", {
                          date: session.updatedAt,
                        })}
                  </>
                }
              />
              <ListItemSecondaryAction>
                <Checkbox
                  color="primary"
                  edge="end"
                  onChange={() => toggleSelectSession(session)}
                  checked={selected.includes(session.id)}
                />
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </StyledDialog>
  );
}

ClosePremiumSessionDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};
